import { DOMUtil } from "@mcleod/core";
import { Component } from "../base/Component";

/**
 * The purpose of this class is to designate components whose related DataSources are allowed to execute a search.
 * This is used in cases like a scrolling tabset, where we might want to scroll past several tabs without loading the
 * data from their related DataSources.
 *
 * The entries in this map are stored in a map where the key is the outer component (such as a Tabset) and the value is
 * the inner component (such as the Tab in the scrolling tabset example).
 */
export class AllowedToSearch {
    private static allowedComponents = new Map<Component, Component>();

    public static add(outerComponent: Component, innerComponent: Component) {
        this.allowedComponents.set(outerComponent, innerComponent);
    }

    public static remove(target: Component | Element) {
        let outerComponent: Component = null;
        const element = this.resolveElement(target);
        this.allowedComponents.forEach((value: Component, key: Component) => {
            if (DOMUtil.isOrContains(value._element, element)) {
                outerComponent = key;
            }
        });
        if (outerComponent != null) {
            this.allowedComponents.delete(outerComponent);
        }
    }

    public static clear() {
        this.allowedComponents.clear();
    }

    public static isAllowed(target: Component | Element): boolean {
        const element = this.resolveElement(target);
        for (const [ outerComponent, innerComponent ] of this.allowedComponents.entries()) {
            // If the test element is within one of the outer components, then it is allowed to search if it is also
            // within the inner component.
            if (DOMUtil.isOrContains(outerComponent._element, element)) {
                return DOMUtil.isOrContains(innerComponent._element, element);
            }
        }
        // If the test element is not within one of the outer components, or if the allowComponents map is empty, then
        // it is allowed to search.
        return true;
    }

    private static resolveElement(input: Component | Element): Element {
        return input instanceof Element ? input : input._element;
    }
}
